import { OeeBatchRepository } from '@/libs/tpm-analytics/data/oee-batch.repository';
import {
    buildUnionIntervalsByWorkplace,
    sumOverlapMinutes,
    computeUnionMinutesByWorkplace,
    computeIntervalIntersectionMinutes,
} from '@/libs/tpm-analytics/core/downtime.utils';
import { buildCycleTimeResolver, buildLocationChainsForShifts } from '@/libs/tpm-analytics/core/cycle-time.resolver';
import { OeeSummaryFilters, OeeWorkplaceSummary } from '@/libs/tpm-analytics/core/types';
import { computeOeeComponents } from '@/libs/tpm-analytics/core/oee.calculator';
import { reportMissingCycleTime, resetMissingCycleTimes } from '@/libs/tpm-analytics/core/missing-cycle-times';

export async function getOeeByWorkplace(filters: OeeSummaryFilters): Promise<OeeWorkplaceSummary[]> {
    // POZNÁMKA: resetMissingCycleTimes() se volá v komponentě na začátku load(), ne zde
    const { shifts, unplanned, planned } = await OeeBatchRepository.fetchCurrent(filters.from, filters.to, {
        locationIds: filters.locationIds || (filters.locationId ? [filters.locationId] : []),
        workplaceIds: filters.workplaceIds || (filters.workplaceId ? [filters.workplaceId] : []),
        projectIds: filters.projectIds,
        shiftTypeIds: filters.shiftTypeIds,
        shiftTeamTypeIds: filters.shiftTeamTypeIds,
    });

    // DŮLEŽITÉ: Akumulujeme SUROVÁ DATA (OK, NOK, minuty), NE procenta!
    type Acc = {
        name: string;
        locationId: string | null;
        totalOk: number;
        totalNok: number;
        totalXMinutes: number;
        totalUnplannedMinutes: number;
        totalIdealRuntimeMinutes: number;
        downtimeMin: number;
    };
    const map = new Map<string | null, Acc>();

    // Předpočítej sjednocené intervaly pro dostupnost v rámci směn
    const plannedUnion = buildUnionIntervalsByWorkplace(planned as any[], filters.from, filters.to);
    const unplannedUnion = buildUnionIntervalsByWorkplace(unplanned as any[], filters.from, filters.to);

    // Připrav resolver CycleTime a location chainy
    const resolveCt = await buildCycleTimeResolver(shifts);
    const locChainsByWp = buildLocationChainsForShifts(shifts);

    const rangeFromMs = Date.parse(filters.from);
    const rangeToMs = Date.parse(filters.to);
    for (const s of shifts) {
        const key: string | null = (s as any).workplaceId ?? s.workplaceId ?? null;
        const name: string = (s as any).workplace?.name ?? '—';
        const locationId: string | null = (s as any).workplace?.location?.id ?? (s as any).workplace?.locationId ?? null;

        if (!map.has(key)) {
            map.set(key, {
                name,
                locationId,
                totalOk: 0,
                totalNok: 0,
                totalXMinutes: 0,
                totalUnplannedMinutes: 0,
                totalIdealRuntimeMinutes: 0,
                downtimeMin: 0,
            });
        }
        const acc = map.get(key)!;

        const sFromMs = Date.parse(s.fromDate as string);
        const sToMs = Date.parse((s.toDate as string) ?? (s.fromDate as string));
        if (!isFinite(sFromMs) || !isFinite(sToMs) || !isFinite(rangeFromMs) || !isFinite(rangeToMs)) continue;
        const fromIso = new Date(Math.max(rangeFromMs, sFromMs)).toISOString();
        const toIso = new Date(Math.min(rangeToMs, sToMs)).toISOString();
        const shiftMinutes = (() => {
            const a = Date.parse(fromIso),
                b = Date.parse(toIso);
            return b > a ? Math.floor((b - a) / 60000) : 0;
        })();
        const plannedMin = sumOverlapMinutes(plannedUnion.get(key), Date.parse(fromIso), Date.parse(toIso));
        let unplannedMin = sumOverlapMinutes(unplannedUnion.get(key), Date.parse(fromIso), Date.parse(toIso));
        // Odečteme překryv plánovaných a neplánovaných odstávek
        const overlapMin = computeIntervalIntersectionMinutes(
            plannedUnion.get(key),
            unplannedUnion.get(key),
            Date.parse(fromIso),
            Date.parse(toIso)
        );
        unplannedMin = Math.max(0, unplannedMin - overlapMin);
        const X = Math.max(0, shiftMinutes - plannedMin);

        const ok = Number(s.okCount ?? 0) || 0;
        const nok = Number(s.nokCount ?? 0) || 0;

        // Ideální runtime přes všechny produkty ve směně pro dané pracoviště
        const prods: any[] = Array.isArray((s as any)?.shiftProductions) ? ((s as any).shiftProductions as any[]) : [];
        const locChain = locChainsByWp.get(key) || [];
        let idealRuntimeMinutes = 0;
        for (const p of prods) {
            const pid: string | null = (p as any)?.productId ?? (p as any)?.product?.id ?? null;
            const pcode: string | null = (p as any)?.product?.code ?? null;
            const pname: string | null = (p as any)?.product?.name ?? null;
            const pcid: string | null = (p as any)?.product?.productCollectionId ?? null;
            const pcname: string | null = (p as any)?.product?.productCollectionName ?? null;
            const count = (Number((p as any)?.okCount ?? 0) || 0) + (Number((p as any)?.nokCount ?? 0) || 0);
            if (!pid || count <= 0) continue;
            
            const wpCode: string | null = (s as any)?.workplace?.code ?? null;
            const wpName: string | null = (s as any)?.workplace?.name ?? null;
            const res = resolveCt({ 
                workplaceId: key, 
                workplaceCode: wpCode,
                workplaceName: wpName,
                productId: pid, 
                productCode: pcode,
                productName: pname,
                productCollectionId: pcid,
                productCollectionName: pcname,
                locationChain: locChain 
            });
            const ctMin = res.minutes;
            if (typeof ctMin === 'number' && ctMin > 0) {
                idealRuntimeMinutes += count * ctMin;
            } else {
                if (res.matchType == null) {
                    const code: string | null = (p as any)?.product?.code ?? null;
                    const wpCode: string | null = (s as any)?.workplace?.code ?? null;
                    reportMissingCycleTime({
                        productId: pid,
                        productCode: code,
                        count,
                        workplaceCodes: wpCode ? [wpCode] : undefined,
                    });
                }
            }
        }

        // Omezíme idealRuntimeMinutes na max dostupný čas pro výrobu (X - neplánované odstávky)
        // aby nedošlo k přetečení do další směny při špatně nastavené normě
        const maxAvailableMinutes = Math.max(0, X - unplannedMin);
        idealRuntimeMinutes = Math.min(idealRuntimeMinutes, maxAvailableMinutes);

        // AKUMULUJEME surová data (ne procenta!)
        acc.totalOk += ok;
        acc.totalNok += nok;
        acc.totalXMinutes += X;
        acc.totalUnplannedMinutes += unplannedMin;
        acc.totalIdealRuntimeMinutes += idealRuntimeMinutes;
    }

    const downByWpUnplanned = computeUnionMinutesByWorkplace(unplanned as any[], filters.from, filters.to);
    for (const [wid, minutes] of downByWpUnplanned.entries()) {
        if (!map.has(wid)) {
            map.set(wid, {
                name: '—',
                locationId: null,
                totalOk: 0,
                totalNok: 0,
                totalXMinutes: 0,
                totalUnplannedMinutes: 0,
                totalIdealRuntimeMinutes: 0,
                downtimeMin: 0,
            });
        }
        const acc = map.get(wid)!;
        acc.downtimeMin += minutes;
    }

    // Teprve TEĎ spočítáme OEE procenta z agregovaných dat pro každé pracoviště
    const result: OeeWorkplaceSummary[] = Array.from(map.entries()).map(([workplaceId, v]) => {
        const comp = computeOeeComponents(
            v.totalOk,
            v.totalNok,
            0, // planByCt - nepoužíváme
            v.totalXMinutes,
            v.totalUnplannedMinutes,
            v.totalIdealRuntimeMinutes
        );
        return {
            workplaceId,
            workplaceName: v.name,
            locationId: v.locationId,
            oee: comp.oee,
            availability: comp.availability,
            performance: comp.performance,
            quality: comp.quality,
            downtimeMinutes: Math.round(v.downtimeMin),
            products: Math.round(v.totalOk + v.totalNok),
            scraps: Math.round(v.totalNok),
        } as OeeWorkplaceSummary;
    });

    return result.sort((a, b) => (b.oee ?? -1) - (a.oee ?? -1));
}

/**
 * Get OEE trend data by workplace over time
 * Returns OEE data for each workplace across time periods (granularity: day/week/month)
 * Used for trend charts where X axis = time periods, Y axis = workplace OEE values
 */
export interface OeeTrendByWorkplacePoint {
    periodKey: string;
    periodLabel: string;
    workplaceData: Map<string | null, {
        workplaceId: string | null;
        workplaceName: string;
        oee: number | null;
        availability: number | null;
        performance: number | null;
        quality: number | null;
    }>;
}

export async function getOeeTrendByWorkplace(filters: OeeSummaryFilters): Promise<OeeTrendByWorkplacePoint[]> {
    const { shifts, unplanned, planned } = await OeeBatchRepository.fetchCurrent(filters.from, filters.to, {
        locationIds: filters.locationIds || (filters.locationId ? [filters.locationId] : []),
        workplaceIds: filters.workplaceIds || (filters.workplaceId ? [filters.workplaceId] : []),
        projectIds: filters.projectIds,
        shiftTypeIds: filters.shiftTypeIds,
        shiftTeamTypeIds: filters.shiftTeamTypeIds,
    });

    const resolveCt = await buildCycleTimeResolver(shifts);
    const locChainsByWp = buildLocationChainsForShifts(shifts);

    const gran: 'day' | 'week' | 'month' = (filters.granularity as any) || 'day';
    
    // Map: periodKey -> workplaceId -> accumulated data
    const acc = new Map<string, Map<string | null, {
        name: string;
        totalOk: number;
        totalNok: number;
        totalXMinutes: number;
        totalUnplannedMinutes: number;
        totalIdealRuntimeMinutes: number;
    }>>();

    const plannedUnion = buildUnionIntervalsByWorkplace(planned as any[], filters.from, filters.to);
    const unplannedUnion = buildUnionIntervalsByWorkplace(unplanned as any[], filters.from, filters.to);

    // Helper functions for period keys
    function dayKey(ms: number): string {
        const d = new Date(ms);
        const dd = d.getDate();
        const m = d.getMonth() + 1;
        return `${d.getFullYear()}-${m < 10 ? '0' + m : m}-${dd < 10 ? '0' + dd : dd}`;
    }
    function monthKey(ms: number): string {
        const d = new Date(ms);
        const m = d.getMonth() + 1;
        return `${d.getFullYear()}-${m < 10 ? '0' + m : m}-01`;
    }
    function isoWeekStartKey(ms: number): string {
        const d = new Date(ms);
        const day = (d.getDay() + 6) % 7;
        const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - day);
        const m = start.getMonth() + 1;
        const dd = start.getDate();
        return `${start.getFullYear()}-${m < 10 ? '0' + m : m}-${dd < 10 ? '0' + dd : dd}`;
    }

    const rangeFromMs = Date.parse(filters.from);
    const rangeToMs = Date.parse(filters.to);

    for (const s of shifts) {
        if (!s?.fromDate) continue;
        const wid: string | null = (s as any).workplaceId ?? s.workplaceId ?? null;
        const wname: string = (s as any).workplace?.name ?? '—';
        
        const sFromMs = Date.parse(s.fromDate as string);
        const sToMs = Date.parse((s.toDate as string) ?? (s.fromDate as string));
        if (!isFinite(sFromMs) || !isFinite(sToMs) || !isFinite(rangeFromMs) || !isFinite(rangeToMs)) continue;
        
        const fromIso = new Date(Math.max(rangeFromMs, sFromMs)).toISOString();
        const toIso = new Date(Math.min(rangeToMs, sToMs)).toISOString();
        const shiftMinutes = (() => {
            const a = Date.parse(fromIso), b = Date.parse(toIso);
            return b > a ? Math.floor((b - a) / 60000) : 0;
        })();

        const plannedMin = sumOverlapMinutes(plannedUnion.get(wid), Date.parse(fromIso), Date.parse(toIso));
        let unplannedMin = sumOverlapMinutes(unplannedUnion.get(wid), Date.parse(fromIso), Date.parse(toIso));
        
        const overlapMin = computeIntervalIntersectionMinutes(
            plannedUnion.get(wid),
            unplannedUnion.get(wid),
            Date.parse(fromIso),
            Date.parse(toIso)
        );
        unplannedMin = Math.max(0, unplannedMin - overlapMin);
        const X = Math.max(0, shiftMinutes - plannedMin);

        const prods: any[] = Array.isArray((s as any)?.shiftProductions) ? ((s as any).shiftProductions as any[]) : [];
        const locChain = locChainsByWp.get(wid) || [];
        let idealRuntimeMinutes = 0;
        
        for (const p of prods) {
            const pid: string | null = (p as any)?.productId ?? (p as any)?.product?.id ?? null;
            const pcode: string | null = (p as any)?.product?.code ?? null;
            const pname: string | null = (p as any)?.product?.name ?? null;
            const pcid: string | null = (p as any)?.product?.productCollectionId ?? null;
            const pcname: string | null = (p as any)?.product?.productCollectionName ?? null;
            const count = (Number((p as any)?.okCount ?? 0) || 0) + (Number((p as any)?.nokCount ?? 0) || 0);
            if (!pid || count <= 0) continue;
            
            const wpCode: string | null = (s as any)?.workplace?.code ?? null;
            const res = resolveCt({ 
                workplaceId: wid,
                workplaceCode: wpCode,
                workplaceName: wname,
                productId: pid, 
                productCode: pcode,
                productName: pname,
                productCollectionId: pcid,
                productCollectionName: pcname,
                locationChain: locChain 
            });
            const ctMin = res.minutes;
            if (typeof ctMin === 'number' && ctMin > 0) {
                idealRuntimeMinutes += count * ctMin;
            } else {
                if (res.matchType == null) {
                    reportMissingCycleTime({
                        productId: pid,
                        productCode: pcode,
                        count,
                        workplaceCodes: wpCode ? [wpCode] : undefined,
                    });
                }
            }
        }

        // Determine period key based on granularity
        const shiftMid = sFromMs + (sToMs - sFromMs) / 2;
        let pkey: string;
        if (gran === 'day') pkey = dayKey(shiftMid);
        else if (gran === 'week') pkey = isoWeekStartKey(shiftMid);
        else pkey = monthKey(shiftMid);

        // Initialize period map if needed
        if (!acc.has(pkey)) {
            acc.set(pkey, new Map());
        }
        const periodMap = acc.get(pkey)!;

        // Initialize workplace data if needed
        if (!periodMap.has(wid)) {
            periodMap.set(wid, {
                name: wname,
                totalOk: 0,
                totalNok: 0,
                totalXMinutes: 0,
                totalUnplannedMinutes: 0,
                totalIdealRuntimeMinutes: 0,
            });
        }

        const wpData = periodMap.get(wid)!;
        wpData.totalOk += prods.reduce((sum, p) => sum + ((p as any)?.okCount ?? 0), 0);
        wpData.totalNok += prods.reduce((sum, p) => sum + ((p as any)?.nokCount ?? 0), 0);
        wpData.totalXMinutes += X;
        wpData.totalUnplannedMinutes += unplannedMin;
        wpData.totalIdealRuntimeMinutes += idealRuntimeMinutes;
    }

    // Convert accumulated data to result format
    const result: OeeTrendByWorkplacePoint[] = [];
    const sortedKeys = Array.from(acc.keys()).sort();

    for (const pkey of sortedKeys) {
        const periodMap = acc.get(pkey)!;
        const workplaceData = new Map<string | null, {
            workplaceId: string | null;
            workplaceName: string;
            oee: number | null;
            availability: number | null;
            performance: number | null;
            quality: number | null;
        }>();

        for (const [wid, data] of periodMap.entries()) {
            const comp = computeOeeComponents(
                data.totalOk,
                data.totalNok,
                0, // planCount - not used in this context
                data.totalXMinutes,
                data.totalUnplannedMinutes,
                data.totalIdealRuntimeMinutes
            );

            workplaceData.set(wid, {
                workplaceId: wid,
                workplaceName: data.name,
                oee: comp.oee,
                availability: comp.availability,
                performance: comp.performance,
                quality: comp.quality,
            });
        }

        result.push({
            periodKey: pkey,
            periodLabel: pkey, // Can be formatted later in component
            workplaceData,
        });
    }

    console.log('[OEE][getOeeTrendByWorkplace] Returning', result.length, 'periods');
    if (result.length > 0) {
        const firstPeriod = result[0];
        const workplaces = Array.from(firstPeriod.workplaceData.values()).map(w => ({ name: w.workplaceName, oee: w.oee }));
        console.log('[OEE][getOeeTrendByWorkplace] First period:', {
            key: firstPeriod.periodKey,
            workplaceCount: firstPeriod.workplaceData.size,
            workplaces: workplaces,
        });
        console.log('[OEE][getOeeTrendByWorkplace] Sample workplace OEE values:', workplaces.slice(0, 3));
    }
    if (result.length > 1) {
        const secondPeriod = result[1];
        const workplaces = Array.from(secondPeriod.workplaceData.values()).map(w => ({ name: w.workplaceName, oee: w.oee }));
        console.log('[OEE][getOeeTrendByWorkplace] Second period:', {
            key: secondPeriod.periodKey,
            workplaceCount: secondPeriod.workplaceData.size,
        });
        console.log('[OEE][getOeeTrendByWorkplace] Sample workplace OEE values:', workplaces.slice(0, 3));
    }

    return result;
}
